import axios from "axios";
import cnBind from "classnames/bind";
import { useFormik } from "formik";

import { API_BASE } from "@/shared/constants/private";
import { Button } from "@/shared/ui/Button";
import { CheckBox } from "@/shared/ui/CheckBox";
import { TextField } from "@/shared/ui/TextField";

import styles from "./Contacts.module.scss";
import { Map, Placemark, YMaps } from "@pbe/react-yandex-maps";
import { useRef, useState  } from "react";
import type ymaps from "yandex-maps";
import { FormFeedback } from "@/components/_Forms/FormFeedback";

const cx = cnBind.bind(styles);

// Координаты для адреса: Санкт-Петербург, Ленинский проспект, 153
const OFFICE_COORDINATES = [59.849966, 30.302950];

export const Contacts = () => {
    const formik = useFormik({
        initialValues: {
            name: "",
            phone: "",
            policy: false,
        },
        onSubmit: async (values, { setSubmitting }) => {
            await axios.post(`${API_BASE}/mail`, values);
            formik.resetForm();
            setSubmitting(false);
        },
    });

    const mapRef = useRef<ymaps.Map | undefined>(undefined);

    const [mapBehaviors, setMapBehaviors] = useState(["drag", "dblClickZoom"]); // scrollZoom по умолчанию убран

    return (
        <div className={cx("wrapper")}>

            {/* Заголовок страницы */}
            <div className={cx("global-container", "title-section")}>
                <h1>Контакты</h1>
            </div>

            {/* 1 и 2. ОБЪЕДИНЕННЫЙ БЛОК: Карта + Контакты */}
            <div className={cx("global-container")}>
                <div className={cx("combined-card")}>

                    {/* А) Интерактивная карта */}
                    <div className={cx("map-container")}>
                        <YMaps query={{ apikey: "4d365d2d-b723-461d-b441-c61107452f60" }}>
                            <div className={cx("map-canvas")}>
                                <Map
                                    instanceRef={mapRef}
                                    width="100%"
                                    height="100%"
                                    // Передаем динамический массив поведений: колесико заработает только при клике
                                    state={{
                                        center: OFFICE_COORDINATES,
                                        zoom: 16,
                                        behaviors: mapBehaviors
                                    }}
                                    options={{ maxZoom: 18, minZoom: 10, autoFitToViewport: 'always' }}
                                    onLoad={(ymaps) => {
                                        mapRef.current?.container.fitToViewport();
                                    }}
                                    // Кликнули по карте — включаем возможность приближать и ОТДАЛЯТЬ колесиком
                                    onClick={() => {
                                        setMapBehaviors(["drag", "dblClickZoom", "scrollZoom"]);
                                    }}
                                    // Увели курсор с карты — полностью блокируем колесико, чтобы страница листалась дальше
                                    onMouseLeave={() => {
                                        setMapBehaviors(["drag", "dblClickZoom"]);
                                    }}
                                    modules={["control.ZoomControl", "control.FullscreenControl", "geoObject.addon.balloon"]}
                                >
                                    <Placemark
                                        geometry={OFFICE_COORDINATES}
                                        properties={{
                                            balloonContentHeader: "Lift Media Group",
                                            balloonContentBody: "г. Санкт-Петербург, пр-кт Ленинский, д.153, лит. А, офис 646",
                                        }}
                                        state={{ balloonOpen: true }}
                                        options={{
                                            preset: "islands#darkBlueBusinessIcon",
                                            iconColor: "#0B4D8F",
                                            hideIconOnBalloonOpen: false
                                        }}
                                    />
                                </Map>
                            </div>
                        </YMaps>
                    </div>

                    {/* Б) Список контактов с крупными значками */}
                    <div className={cx("contacts-block")}>
                        <div className={cx("contacts-line")}>
                            {/* Адрес */}
                            <div className={cx("social-item")}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://w3.org">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#FFFFFF"/>
                                </svg>
                                <span>196240, г.Санкт-Петербург, вн.тер.г. муниципальный округ Новоизмайловское, пр-кт Ленинский, д. 153, стр. 1, помещ. 188Н (офис 646)</span>
                            </div>

                            {/* Телефон */}
                            <div className={cx("social-item")}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://w3.org">
                                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="#FFFFFF"/>
                                </svg>
                                <a href="tel:+78129820058"><span>+7 (812) 982-00-58</span></a>
                            </div>

                            {/* Почта */}
                            <div className={cx("social-item")}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://w3.org">
                                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5V8z" fill="#FFFFFF"/>
                                </svg>
                                <a href="mailto:sales@liftmg.ru"><span>sales@liftmg.ru</span></a>
                            </div>
                        </div>

                        {/* Мессенджеры прижаты к верхнему краю */}
                        <div className={cx("messenger-row")}>
                            <div onClick={() => window.open("https://max.ru/u/f9LHodD0cOKEIQ90ZSTukfL61T1F9ZPXCnD6dnVQgDd-TIatgt9F6cH863Q", "_blank")} className={cx("messenger-card")}>
                                <svg width="22" height="22" viewBox="0 0 40 40" fill="none" xmlns="http://w3.org">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20C0 8.95431 8.95431 0 20 0ZM20 9C13.9248 9 9 13.9248 9 20C9 22.4283 9.78762 24.6729 11.1215 26.4952L9.26123 30.2158C9.03456 30.6691 9.42168 31.1873 9.91745 31.0634L14.0725 30.0246C15.8239 30.6542 17.8202 31 20 31C26.0752 31 31 26.0752 31 20C31 13.9248 26.0752 9 20 9ZM12.6 20C12.6 15.9131 15.9131 12.6 20 12.6C24.0869 12.6 27.4 15.9131 27.4 20C27.4 24.0869 24.0869 27.4 20 27.4C18.4239 27.4 16.966 26.9077 15.7656 26.0683L15.4385 25.84L12.0121 26.6966L13.5413 23.6383L13.2577 23.2189C12.8344 22.2335 12.6 21.1448 12.6 20Z" fill="#FFFFFF"/>
                                </svg>
                                <span>MAX</span>
                            </div>
                            <div onClick={() => window.open("https://t.me/liftmediagroup", "_blank")} className={cx("messenger-card")}>
                                <svg width="22" height="22" viewBox="0 0 40 40" fill="none" xmlns="http://w3.org">
                                    <path d="M20 0C8.954 0 0 8.954 0 20s8.954 20 20 20 20-8.954 20-20S31.046 0 20 0Zm9.27 13.602-3.158 14.89c-.238 1.054-.863 1.316-.167.685l-4.812-3.546-2.321 2.233c-.257.257-.473.473-.972.473l.345-4.89 8.902-8.041c.387-.343-.083-.534-.6-.19L15.485 21.94l-4.738-1.482c-1.03-.322-1.05-.1.215-.523L29.243 12.39c.857-.322 1.605.19 1.297 1.554Z" fill="#FFFFFF"/>
                                </svg>
                                <span>Telegram</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. ФОРМА ОБРАТНОЙ СВЯЗИ */}
            <FormFeedback />
        </div>
    );
};
